import { createFileRoute } from "@tanstack/react-router";
import { seoHead } from "@/lib/seo";
import { useQuery } from "@tanstack/react-query";
import {
  COFFMAN_COVE,
  fetchMarineConditions,
  fetchMarineWeather,
  fetchTideHeights,
  fetchTides,
  moonPhase,
  moonPhaseName,
  pressureTrendFromHourly,
  scoreSpots,
  type TideHeight,
  type TidePrediction,
} from "@/lib/fishing-data";

import { recommendForHighTideWindows, findBestFishingWindows, type WindowRecommendation, type BestHourWindow } from "@/lib/fishing-data";
import { SpotMapClient as SpotMap } from "@/components/SpotMapClient";
import { SentryBoundary } from "@/components/SentryBoundary";
import { ReportNarrativeCard } from "@/components/ReportNarrativeCard";
import { DailyAlmanac } from "@/components/DailyAlmanac";


import { LiveDataStatus, formatRelative } from "@/components/LiveDataStatus";
import { ShareReport } from "@/components/ShareReport";
import { runLiveDataRetry } from "@/lib/live-data-retry";
import {
  AlertTriangle,
  RefreshCw,
  Anchor,
  ArrowDown,
  ArrowUp,
  Clock,
  CloudRain,
  Compass,
  Droplets,
  Eye,
  Fish,
  Gauge,
  Loader2,
  MapPin,
  Minus,
  Moon,
  Sunrise,
  Sunset,
  Target,
  Thermometer,
  Waves,
  Wind,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  useConditionsThresholds,
  ThresholdsPanel,
  ThresholdsToggleButton,
} from "@/components/ConditionsThresholds";


export const Route = createFileRoute("/fishing-report")({
  head: () =>
    seoHead({
      path: "/fishing-report",
      title: "Live Fishing Report — Coffman Cove Lodging",
      description:
        "Real-time weather, tides, swell, barometric pressure and AI-ranked fishing spot suggestions for Coffman Cove, Alaska.",
    }),
  component: FishingReport,
});

// Scheduled auto-refresh slots in Coffman Cove local time (America/Anchorage).
// Data pulls happen at 5 AM, 2 PM, and 7 PM AKT rather than on a fixed interval.
const REFRESH_SLOT_HOURS = [5, 14, 19] as const;
const REFRESH_SLOT_LABEL = "5 AM · 2 PM · 7 PM AKT";
const AUTO_REFRESH_KEY = "ccl.fishingReport.autoRefresh";
const TZ_MODE_KEY = "ccl.fishingReport.tzMode";

function msUntilNextScheduledRefresh(from: number = Date.now()): number {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: COFFMAN_COVE.timezone,
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const parts = fmt.formatToParts(new Date(from));
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  const nowSec = (get("hour") % 24) * 3600 + get("minute") * 60 + get("second");
  const slotsSec = REFRESH_SLOT_HOURS.map((h) => h * 3600);
  const next = slotsSec.find((s) => s > nowSec);
  const deltaSec = next != null ? next - nowSec : 24 * 3600 - nowSec + slotsSec[0];
  return Math.max(1000, deltaSec * 1000);
}

function nextScheduledRefreshLabel(from: number = Date.now()): string {
  const ms = msUntilNextScheduledRefresh(from);
  const target = new Date(from + ms);
  return target.toLocaleTimeString("en-US", {
    timeZone: COFFMAN_COVE.timezone,
    hour: "numeric",
    minute: "2-digit",
  });
}


function FishingReport() {
  // Auto-refresh: on by default, persisted, applied as Query refetchInterval.
  const [autoRefresh, setAutoRefresh] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    const v = window.localStorage.getItem(AUTO_REFRESH_KEY);
    return v === null ? true : v === "1";
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(AUTO_REFRESH_KEY, autoRefresh ? "1" : "0");
  }, [autoRefresh]);

  // Timezone display mode for "Last updated": "cove" (America/Anchorage) or "local".
  const [tzMode, setTzMode] = useState<"cove" | "local">(() => {
    if (typeof window === "undefined") return "cove";
    const v = window.localStorage.getItem(TZ_MODE_KEY);
    return v === "local" ? "local" : "cove";
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(TZ_MODE_KEY, tzMode);
  }, [tzMode]);
  const localTz = typeof Intl !== "undefined"
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : "local";


  // Tick once a minute so "Updated X min ago" labels stay accurate between
  // refetches without remounting the cards.
  const [, setNowTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setNowTick((n) => n + 1), 60_000);
    return () => window.clearInterval(id);
  }, []);

  // User-editable ideal-range thresholds for the At-a-glance banner.
  const { thresholds, save: saveThresholds, reset: resetThresholds } = useConditionsThresholds();
  const [thresholdsOpen, setThresholdsOpen] = useState(false);


  const refetchInterval = autoRefresh ? (() => msUntilNextScheduledRefresh()) : false as const;

  const weather = useQuery({
    queryKey: ["weather"],
    queryFn: ({ signal }) => fetchMarineWeather(signal),
    staleTime: 10 * 60_000,
    refetchInterval,
    refetchIntervalInBackground: false,
  });
  const marine = useQuery({
    queryKey: ["marine"],
    queryFn: ({ signal }) => fetchMarineConditions(signal),
    staleTime: 10 * 60_000,
    refetchInterval,
    refetchIntervalInBackground: false,
  });
  const tides = useQuery({
    queryKey: ["tides"],
    queryFn: ({ signal }) => fetchTides(signal),
    staleTime: 30 * 60_000,
    refetchInterval,
    refetchIntervalInBackground: false,
  });
  const tideHeights = useQuery({
    queryKey: ["tide-heights"],
    queryFn: ({ signal }) => fetchTideHeights(signal),
    staleTime: 30 * 60_000,
    refetchInterval,
    refetchIntervalInBackground: false,
  });


  const [target, setTarget] = useState<"king" | "halibut">("king");
  const [isRetrying, setIsRetrying] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRetry = async () => {
    if (isRetrying) return;
    setIsRetrying(true);
    try {
      await runLiveDataRetry({ weather, marine, tides });
    } finally {
      setIsRetrying(false);
    }
  };

  const handleRefreshNow = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await Promise.allSettled([
        weather.refetch(),
        marine.refetch(),
        tides.refetch(),
        tideHeights.refetch(),
      ]);
    } finally {
      setIsRefreshing(false);
    }
  };


  const suggestions = useMemo(() => {
    if (!weather.data || !tides.data) return [];
    const trend = pressureTrendFromHourly(weather.data.hourly);
    return scoreSpots({
      tides: tides.data,
      pressureMb: weather.data.current.pressure_msl,
      pressureTrend: trend.trend,
      windKn: weather.data.current.wind_speed_10m,
      target,
    });
  }, [weather.data, tides.data, target]);

  const windowRecs = useMemo<WindowRecommendation[]>(() => {
    if (!weather.data || !tides.data) return [];
    return recommendForHighTideWindows({
      tides: tides.data,
      hourly: weather.data.hourly,
      target,
    });
  }, [weather.data, tides.data, target]);

  const bestWindows = useMemo(() => {
    if (!weather.data || !tides.data) return { king: [], halibut: [] };
    return {
      king: findBestFishingWindows({ tides: tides.data, hourly: weather.data.hourly, target: "king", count: 4 }),
      halibut: findBestFishingWindows({ tides: tides.data, hourly: weather.data.hourly, target: "halibut", count: 4 }),
    };
  }, [weather.data, tides.data]);

  const shareSummary = useMemo(() => {
    const lines: string[] = [`Coffman Cove, AK — Fishing Report`];
    if (weather.data) {
      const c = weather.data.current;
      lines.push(
        `Now: ${Math.round(c.temperature_2m)}°F · Wind ${Math.round(c.wind_speed_10m)} kn · ${Math.round(c.pressure_msl)} hPa`,
      );
    }
    if (tides.data?.length) {
      const next = tides.data.find((t) => new Date(t.t).getTime() > Date.now());
      if (next) {
        const time = new Date(next.t).toLocaleTimeString("en-US", {
          timeZone: COFFMAN_COVE.timezone,
          hour: "numeric",
          minute: "2-digit",
        });
        lines.push(`Next tide: ${next.type === "H" ? "High" : "Low"} ${time} (${Number(next.v).toFixed(1)} ft)`);
      }
    }
    const topKing = bestWindows.king[0];
    if (topKing) {
      const t = new Date(topKing.startMs).toLocaleTimeString("en-US", {
        timeZone: COFFMAN_COVE.timezone, hour: "numeric", minute: "2-digit",
      });
      lines.push(`Best King window: ${t} (score ${topKing.score})`);
    }
    const topHal = bestWindows.halibut[0];
    if (topHal) {
      const t = new Date(topHal.startMs).toLocaleTimeString("en-US", {
        timeZone: COFFMAN_COVE.timezone, hour: "numeric", minute: "2-digit",
      });
      lines.push(`Best Halibut window: ${t} (score ${topHal.score})`);
    }
    if (suggestions[0]) {
      lines.push(`Top spot (${target}): ${suggestions[0].spot.name} — score ${suggestions[0].score}`);
    }
    return lines.join("\n");
  }, [weather.data, tides.data, bestWindows, suggestions, target]);


  return (
    <div
      className="print-area text-white [&_h1]:text-white [&_h2]:text-white [&_h3]:text-white [&_h4]:text-white [&_p]:text-white [&_li]:text-white [&_th]:text-white [&_td]:text-white"
      style={{
        // Force pure-white foreground tokens on this page only
        ["--foreground" as any]: "oklch(1 0 0)",
        ["--card-foreground" as any]: "oklch(1 0 0)",
        ["--popover-foreground" as any]: "oklch(1 0 0)",
        ["--muted-foreground" as any]: "oklch(0.92 0 0)",
      }}
    >
      <section className="bg-[var(--color-deep)] text-[var(--color-parchment)]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-16 sm:pt-20 pb-12 sm:pb-16">
          <p className="uppercase tracking-[0.3em] text-[10px] sm:text-xs opacity-70">Live Conditions</p>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl mt-3 text-balance text-white leading-[1.05]">
            Today on the water.
          </h1>
          <p className="mt-4 max-w-prose text-sm sm:text-base opacity-90">
            Real-time weather, swell, tides and barometric trend for Coffman Cove
            ({COFFMAN_COVE.lat.toFixed(3)}°N, {Math.abs(COFFMAN_COVE.lon).toFixed(3)}°W). Tides
            pulled at NOAA CO-OPS 6-minute predicted resolution from station{" "}
            {COFFMAN_COVE.tideStationName}. Spot rankings blend live tide phase, barometric
            trend, moon phase, wind and ADF&G-referenced historical King &amp; Halibut
            productivity for waters within 25 mi of the Coffman Cove dock. Updated every 30
            minutes.
          </p>

          {(() => {
            const latest = Math.max(
              weather.dataUpdatedAt || 0,
              marine.dataUpdatedAt || 0,
              tides.dataUpdatedAt || 0,
            );
            if (!latest) return null;
            const activeTz = tzMode === "local" ? localTz : COFFMAN_COVE.timezone;
            const tzTime = new Date(latest).toLocaleString("en-US", {
              timeZone: activeTz,
              month: "short",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
              timeZoneName: "short",
            });
            const nextRefreshMs = autoRefresh ? msUntilNextScheduledRefresh() : 0;
            const nextAt = autoRefresh ? nextScheduledRefreshLabel() : "";
            const nextMin = Math.ceil(nextRefreshMs / 60_000);

            const ageMin = Math.floor((Date.now() - latest) / 60_000);
            const freshness =
              ageMin < 30
                ? { label: "Fresh", cls: "border-emerald-400/60 bg-emerald-400/15 text-emerald-100", dot: "bg-emerald-400" }
                : ageMin < 60
                ? { label: "Stale", cls: "border-amber-400/60 bg-amber-400/15 text-amber-100", dot: "bg-amber-400" }
                : { label: "Outdated", cls: "border-red-400/60 bg-red-400/15 text-red-100", dot: "bg-red-400" };
            return (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <p className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-3 py-1 text-[11px] sm:text-xs font-mono text-white/90">
                  <Clock size={12} />
                  <span>Last updated {tzTime}</span>
                  <span className="opacity-60">· {activeTz}</span>
                </p>
                <div
                  className="inline-flex items-center rounded-full border border-white/20 bg-white/5 p-0.5 text-[11px] sm:text-xs font-mono"
                  role="group"
                  aria-label="Timezone display"
                  title={`Local: ${localTz}`}
                >
                  <button
                    type="button"
                    onClick={() => setTzMode("cove")}
                    aria-pressed={tzMode === "cove"}
                    className={`rounded-full px-2.5 py-1 min-h-8 transition ${
                      tzMode === "cove" ? "bg-[var(--color-electric)]/30 text-white" : "text-white/70 hover:text-white"
                    }`}
                  >
                    Coffman Cove
                  </button>
                  <button
                    type="button"
                    onClick={() => setTzMode("local")}
                    aria-pressed={tzMode === "local"}
                    className={`rounded-full px-2.5 py-1 min-h-8 transition ${
                      tzMode === "local" ? "bg-[var(--color-electric)]/30 text-white" : "text-white/70 hover:text-white"
                    }`}
                  >
                    My local
                  </button>
                </div>
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] sm:text-xs font-mono ${freshness.cls}`}
                  title={`Data is ${ageMin} min old`}
                  aria-label={`Data freshness: ${freshness.label}, ${ageMin} minutes old`}
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${freshness.dot} ${freshness.label === "Fresh" ? "animate-pulse" : ""}`} />
                  <span>{freshness.label}</span>
                  <span className="opacity-70">· {ageMin < 1 ? "just now" : `${ageMin} min old`}</span>
                </span>
                <button
                  type="button"
                  onClick={() => setAutoRefresh((v) => !v)}
                  aria-pressed={autoRefresh}
                  title={autoRefresh ? "Click to pause auto-refresh" : "Click to resume auto-refresh"}
                  className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 min-h-9 text-[11px] sm:text-xs font-mono transition ${
                    autoRefresh
                      ? "border-[var(--color-ember)]/60 bg-[var(--color-ember)]/15 text-white hover:bg-[var(--color-ember)]/25"
                      : "border-white/20 bg-white/5 text-white/80 hover:bg-white/10"
                  }`}
                >
                  <RefreshCw
                    size={12}
                    className={autoRefresh && (weather.isFetching || marine.isFetching || tides.isFetching) ? "animate-spin" : ""}
                  />
                  {autoRefresh ? (
                    <span>
                      Auto-refresh: On · {REFRESH_SLOT_LABEL}
                      {nextAt && (
                        <span className="opacity-70"> · next at {nextAt}{nextMin > 0 ? ` (in ${nextMin} min)` : ""}</span>
                      )}
                    </span>
                  ) : (
                    <span>Auto-refresh: Off</span>
                  )}

                </button>
                <button
                  type="button"
                  onClick={handleRefreshNow}
                  disabled={isRefreshing}
                  title="Re-fetch all data now"
                  className="inline-flex items-center gap-2 rounded-full border border-[var(--color-electric)]/60 bg-[var(--color-electric)]/15 px-3 py-1 min-h-9 text-[11px] sm:text-xs font-mono text-white transition hover:bg-[var(--color-electric)]/25 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <RefreshCw size={12} className={isRefreshing ? "animate-spin" : ""} />
                  <span>{isRefreshing ? "Refreshing…" : "Refresh now"}</span>
                </button>
              </div>
            );
          })()}
          <ShareReport summary={shareSummary} />
        </div>
      </section>

      {/* Live data status / fallback */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 -mt-10">
        <LiveDataStatus
          weatherError={weather.error as Error | null}
          marineError={marine.error as Error | null}
          tidesError={tides.error as Error | null}
          tideHeightsError={tideHeights.error as Error | null}
          isFetching={weather.isFetching || marine.isFetching || tides.isFetching || tideHeights.isFetching}
          isRetrying={isRetrying}
          updatedAt={Math.max(weather.dataUpdatedAt || 0, marine.dataUpdatedAt || 0, tides.dataUpdatedAt || 0, tideHeights.dataUpdatedAt || 0)}
          hasCachedData={!!(weather.data || marine.data || tides.data || tideHeights.data)}
          onRetry={handleRetry}
        />
      </section>

      {/* Fisherman's daily almanac */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 mt-6">
        <DailyAlmanac />
      </section>



      {/* At-a-glance conditions banner */}
      {weather.data && (() => {
        const c = weather.data.current;
        const wind = Math.round(c.wind_speed_10m ?? 0);
        const gusts = Math.round(c.wind_gusts_10m ?? 0);
        const precip = Number(c.precipitation ?? 0);
        const code = c.weather_code as number | undefined;

        type Alert = { icon: typeof Wind; label: string; detail: string; tone: "warn" | "bad" };
        const alerts: Alert[] = [];

        // Wind — thresholds are user-configurable via the settings panel.
        if (wind >= thresholds.windBadKn || gusts >= thresholds.gustBadKn) {
          alerts.push({
            icon: Wind,
            label: "Wind: rough",
            detail: `${wind} kn sustained${gusts ? `, gusts ${gusts} kn` : ""} — expect chop`,
            tone: "bad",
          });
        } else if (wind >= thresholds.windCautionKn) {
          alerts.push({
            icon: Wind,
            label: "Wind: brisk",
            detail: `${wind} kn — manageable, watch exposed water`,
            tone: "warn",
          });
        }

        // Precipitation — user-configurable hourly amber/red thresholds.
        if (precip >= thresholds.precipBadIn) {
          alerts.push({
            icon: CloudRain,
            label: "Precipitation: heavy",
            detail: `${precip.toFixed(2)}" last hour — pack rain gear`,
            tone: "bad",
          });
        } else if (precip >= thresholds.precipCautionIn) {
          alerts.push({
            icon: CloudRain,
            label: "Precipitation: showers",
            detail: `${precip.toFixed(2)}" last hour`,
            tone: "warn",
          });
        }

        // Visibility — inferred from weather code, toggleable.
        const foggy = code !== undefined && [45, 48].includes(code);
        const rainReduced = code !== undefined && [61, 63, 65, 80, 81, 82, 95].includes(code);
        if (foggy && thresholds.alertOnFog) {
          alerts.push({
            icon: Eye,
            label: "Visibility: low",
            detail: "Fog reported — run slow and use radar",
            tone: "bad",
          });
        } else if (rainReduced && thresholds.alertOnRainReduced) {
          alerts.push({
            icon: Eye,
            label: "Visibility: reduced",
            detail: "Rain limits sight lines",
            tone: "warn",
          });
        }

        const tone =
          alerts.some((a) => a.tone === "bad")
            ? "bad"
            : alerts.length
            ? "warn"
            : "good";

        const shell =
          tone === "bad"
            ? "border-red-400/60 bg-red-500/15"
            : tone === "warn"
            ? "border-amber-400/60 bg-amber-500/15"
            : "border-emerald-400/60 bg-emerald-500/15";

        return (
          <section className="mx-auto max-w-7xl px-4 sm:px-6 mt-6">
            <div className={`rounded-2xl border ${shell} p-4 sm:p-5 backdrop-blur`}>
              <div className="flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-white/80">
                  <AlertTriangle size={14} />
                  At-a-glance conditions
                </span>
                {alerts.length === 0 ? (
                  <span className="text-sm text-emerald-100 font-medium">
                    Ideal window — wind, precipitation and visibility all in range.
                  </span>
                ) : (
                  <span className="text-sm text-white/80">
                    {alerts.length} factor{alerts.length > 1 ? "s" : ""} outside the ideal range
                  </span>
                )}
                <ThresholdsToggleButton
                  onClick={() => setThresholdsOpen((v) => !v)}
                  active={thresholdsOpen}
                />
              </div>

              {alerts.length > 0 && (
                <div className="mt-3 grid gap-2 sm:grid-cols-3">
                  {alerts.map((a) => {
                    const Icon = a.icon;
                    const chip =
                      a.tone === "bad"
                        ? "border-red-400/60 bg-red-400/10 text-red-50"
                        : "border-amber-400/60 bg-amber-400/10 text-amber-50";
                    return (
                      <div
                        key={a.label}
                        className={`rounded-xl border ${chip} px-3 py-2 flex items-start gap-2`}
                      >
                        <Icon size={16} className="mt-0.5 shrink-0" />
                        <div className="text-sm leading-snug">
                          <div className="font-semibold">{a.label}</div>
                          <div className="opacity-90">{a.detail}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <ThresholdsPanel
                open={thresholdsOpen}
                onClose={() => setThresholdsOpen(false)}
                thresholds={thresholds}
                onSave={saveThresholds}
                onReset={resetThresholds}
              />
            </div>
          </section>
        );
      })()}




      {/* Best Fishing Locations — heat-map on topo */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 mt-8">
        <div className="mb-4">
          <p className="uppercase tracking-[0.3em] text-xs text-[var(--color-ember)]">
            Best fishing locations
          </p>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl mt-2 text-balance">
            Where today's conditions and historical pulls point.
          </h2>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Heat-map dots within 25 mi of the Coffman Cove dock — ranked daily by tide phase,
            barometric trend, wind, moon, and local catch-log history for King Salmon and Halibut.
            Toggle a species to re-rank.
          </p>
        </div>
        <SentryBoundary name="fishing_report.spot_map">
          <SpotMap
            tides={tides.data ?? undefined}
            conditions={
              weather.data
                ? {
                    pressureMb: weather.data.current.pressure_msl,
                    pressureTrend: pressureTrendFromHourly(weather.data.hourly).trend,
                    windKn: weather.data.current.wind_speed_10m,
                  }
                : undefined
            }
          />
        </SentryBoundary>
      </section>




      {/* Stored narrative */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 mt-6">
        <ReportNarrativeCard />
      </section>

      {/* Widgets */}

      <section className="mx-auto max-w-7xl px-4 sm:px-6 mt-6 pb-12">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <WeatherCard data={weather.data} loading={weather.isLoading} isFetching={weather.isFetching} updatedAt={weather.dataUpdatedAt} />
          <PressureCard data={weather.data} loading={weather.isLoading} isFetching={weather.isFetching} updatedAt={weather.dataUpdatedAt} />
          <WindCard data={weather.data} loading={weather.isLoading} isFetching={weather.isFetching} updatedAt={weather.dataUpdatedAt} />
          <SeaCard data={marine.data} weather={weather.data} loading={marine.isLoading} isFetching={marine.isFetching} updatedAt={marine.dataUpdatedAt} />
        </div>
      </section>


      {/* Next tide status */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 pb-6">
        <TideStatusCard tides={tides.data} loading={tides.isLoading} isFetching={tides.isFetching} updatedAt={tides.dataUpdatedAt} />
      </section>

      {/* 48-hour tide chart */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 pb-6">
        <TideChartCard
          heights={tideHeights.data}
          extremes={tides.data}
          loading={tideHeights.isLoading}
          isFetching={tideHeights.isFetching}
          updatedAt={tideHeights.dataUpdatedAt}
        />
      </section>



      {/* Tides + Sun/Moon */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 pb-12 grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2 min-w-0">
          <TideCard tides={tides.data} heights={tideHeights.data} loading={tides.isLoading} isFetching={tides.isFetching} updatedAt={tides.dataUpdatedAt} />
        </div>
        <div className="min-w-0">
          <SunMoonCard data={weather.data} loading={weather.isLoading} isFetching={weather.isFetching} updatedAt={weather.dataUpdatedAt} />
        </div>
      </section>

      {/* Best Fishing Windows */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 pb-12">
        <BestWindowsCard
          king={bestWindows.king}
          halibut={bestWindows.halibut}
          loading={weather.isLoading || tides.isLoading}
          isFetching={weather.isFetching || tides.isFetching}
          updatedAt={Math.min(weather.dataUpdatedAt || 0, tides.dataUpdatedAt || 0) || Date.now()}
        />
      </section>

      {/* 24h Pressure Trend */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 pb-12">
        <PressureTrendCard
          data={weather.data}
          loading={weather.isLoading}
          isFetching={weather.isFetching}
          updatedAt={weather.dataUpdatedAt}
        />
      </section>





      {/* 7-day Forecast */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 pb-12">
        <SevenDayForecast
          data={weather.data}
          loading={weather.isLoading}
          error={weather.error as Error | null}
          isFetching={weather.isFetching}
          onRetry={() => weather.refetch()}
        />
      </section>

      {/* Spot Finder — high tide windows */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 pb-12">
        <SpotFinder recs={windowRecs} target={target} setTarget={setTarget} loading={weather.isLoading || tides.isLoading} />
      </section>




      {/* Data source verification */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 pb-24">
        <DataSourceVerification
          weather={weather}
          marine={marine}
          tides={tides}
        />
      </section>
    </div>
  );
}

/* ---------- data source verification ---------- */

function DataSourceVerification({
  weather,
  marine,
  tides,
}: {
  weather: { dataUpdatedAt: number; error: unknown; isFetching: boolean };
  marine: { dataUpdatedAt: number; error: unknown; isFetching: boolean };
  tides: { dataUpdatedAt: number; error: unknown; isFetching: boolean };
}) {
  const lat = COFFMAN_COVE.lat.toFixed(4);
  const lon = Math.abs(COFFMAN_COVE.lon).toFixed(4);
  const rows = [
    {
      label: "Weather + Forecast",
      source: "Open-Meteo Forecast API",
      endpoint: "api.open-meteo.com/v1/forecast",
      target: `${lat}°N, ${lon}°W (Coffman Cove grid cell)`,
      tz: COFFMAN_COVE.timezone,
      updatedAt: weather.dataUpdatedAt,
      error: weather.error as Error | null,
      fetching: weather.isFetching,
    },
    {
      label: "Marine (swell / sea)",
      source: "Open-Meteo Marine API",
      endpoint: "marine-api.open-meteo.com/v1/marine",
      target: `${lat}°N, ${lon}°W (Clarence Strait)`,
      tz: COFFMAN_COVE.timezone,
      updatedAt: marine.dataUpdatedAt,
      error: marine.error as Error | null,
      fetching: marine.isFetching,
    },
    {
      label: "Tides",
      source: "NOAA CO-OPS Tides & Currents",
      endpoint: "api.tidesandcurrents.noaa.gov",
      target: `Station ${COFFMAN_COVE.tideStation} — ${COFFMAN_COVE.tideStationName}`,
      tz: COFFMAN_COVE.timezone,
      updatedAt: tides.dataUpdatedAt,
      error: tides.error as Error | null,
      fetching: tides.isFetching,
    },
  ];

  return (
    <details className="rounded-xl border border-border bg-card p-5 md:p-6 group" open>
      <summary className="cursor-pointer list-none flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-[var(--color-ember)] min-w-0">
          <Target size={16} className="shrink-0" />
          <p className="uppercase tracking-[0.2em] text-[11px] font-semibold truncate">
            Data source verification — Coffman Cove
          </p>
        </div>
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground shrink-0 group-open:hidden">
          Show
        </span>
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground shrink-0 hidden group-open:inline">
          Hide
        </span>
      </summary>

      <p className="mt-3 text-xs text-muted-foreground">
        Live confirmation of the exact coordinates and station IDs queried for every
        dataset rendered above. If any row shows an unexpected location, the targeting is wrong.
      </p>

      <div className="mt-4 overflow-x-auto -mx-4 md:-mx-6 px-4 md:px-6">
        <table className="w-full min-w-[640px] text-xs">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
              <th className="py-2 pr-4 font-semibold">Dataset</th>
              <th className="py-2 pr-4 font-semibold">Source</th>
              <th className="py-2 pr-4 font-semibold">Target</th>
              <th className="py-2 pr-4 font-semibold">Timezone</th>
              <th className="py-2 pr-4 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label} className="border-b border-border/60 align-top">
                <td className="py-3 pr-4 font-semibold text-primary">
                  <div>{r.label}</div>
                  <div className="mt-1 inline-flex items-center gap-1 rounded-full border border-border bg-background px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <MapPin size={9} /> Coffman Cove, Alaska
                  </div>
                </td>
                <td className="py-3 pr-4 text-muted-foreground">
                  <div>{r.source}</div>
                  <div className="font-mono text-[10px] opacity-70 mt-0.5">{r.endpoint}</div>
                </td>
                <td className="py-3 pr-4 font-mono text-[11px]">
                  <div>{r.target}</div>
                  <div className="mt-1 text-[10px] opacity-70 not-italic">Coffman Cove, Alaska</div>
                </td>
                <td className="py-3 pr-4 font-mono text-[11px] text-muted-foreground">{r.tz}</td>

                <td className="py-3 pr-4">
                  {r.error ? (
                    <span className="inline-flex items-center gap-1 text-destructive">
                      <AlertTriangle size={12} /> error
                    </span>
                  ) : r.fetching && !r.updatedAt ? (
                    <span className="inline-flex items-center gap-1 text-muted-foreground">
                      <RefreshCw size={12} className="animate-spin" /> loading
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[var(--color-ember)]">
                      <span className="h-1.5 w-1.5 rounded-full bg-[var(--color-ember)]" />
                      live · {formatRelative(r.updatedAt)}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-4 text-[10px] text-muted-foreground font-mono">
        Reference point: Coffman Cove, Prince of Wales Island, AK · {lat}°N, {lon}°W · TZ {COFFMAN_COVE.timezone}
      </p>
    </details>
  );
}



/* ---------- widgets ---------- */


function Widget({
  icon: Icon,
  label,
  children,
  loading,
  isFetching,
  source,
  updatedAt,
}: {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
  children: React.ReactNode;
  loading?: boolean;
  isFetching?: boolean;
  source?: React.ReactNode;
  updatedAt?: number;
}) {
  const refreshing = !!isFetching && !loading;
  return (
    <div className="group rounded-xl bg-card border border-border p-5 shadow-sm relative overflow-hidden transition-colors hover:border-[var(--color-ember)] flex flex-col" aria-busy={loading || refreshing}>
      <span className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-primary via-[var(--color-ember)] to-primary opacity-80" />
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[var(--color-ember)] min-w-0">
          <Icon size={16} className="shrink-0" />
          <p className="uppercase tracking-[0.2em] text-[11px] font-semibold truncate">{label}</p>
          {refreshing && (
            <Loader2 size={12} className="shrink-0 animate-spin opacity-80" aria-label="Refreshing" />
          )}
        </div>
        <span className="shrink-0 inline-flex items-center gap-1 rounded-full border border-border bg-background px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
          <MapPin size={9} /> Coffman Cove, AK
        </span>
      </div>

      {loading ? (
        <div className="mt-3 space-y-2" aria-hidden>
          <div className="h-6 w-2/3 rounded bg-muted animate-pulse" />
          <div className="h-4 w-1/2 rounded bg-muted animate-pulse" />
          <div className="h-4 w-3/4 rounded bg-muted animate-pulse" />
        </div>
      ) : (
        <div className="mt-3 text-primary">{children}</div>
      )}
      {(source || updatedAt) && (
        <div className="mt-4 pt-3 border-t border-border/60 space-y-1">
          {updatedAt ? (
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--color-ember)]">
              Updated {formatRelative(updatedAt)}{refreshing ? " · refreshing…" : ""}
            </p>
          ) : null}
          {source && (
            <p className="text-[10px] font-mono text-muted-foreground break-words leading-tight">{source}</p>
          )}
        </div>
      )}
    </div>
  );
}


const OPEN_METEO_PROVENANCE = (
  <>
    Open-Meteo · {COFFMAN_COVE.lat.toFixed(4)}°N, {Math.abs(COFFMAN_COVE.lon).toFixed(4)}°W · TZ {COFFMAN_COVE.timezone}
  </>
);
const OPEN_METEO_MARINE_PROVENANCE = (
  <>
    Open-Meteo Marine · {COFFMAN_COVE.lat.toFixed(4)}°N, {Math.abs(COFFMAN_COVE.lon).toFixed(4)}°W · TZ {COFFMAN_COVE.timezone}
  </>
);
const NOAA_TIDE_PROVENANCE = (
  <>
    NOAA CO-OPS station {COFFMAN_COVE.tideStation} · {COFFMAN_COVE.tideStationName} · TZ {COFFMAN_COVE.timezone}
  </>
);


function WeatherCard({ data, loading, isFetching, updatedAt }: { data: any; loading?: boolean; isFetching?: boolean; updatedAt?: number }) {
  return (
    <Widget icon={Thermometer} label="Air Temperature" loading={loading} isFetching={isFetching} source={OPEN_METEO_PROVENANCE} updatedAt={updatedAt}>
      {data && (
        <>
          <p className="font-display text-3xl sm:text-4xl">
            {Math.round(data.current.temperature_2m)}°F
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            Feels {Math.round(data.current.apparent_temperature)}°F · {data.current.relative_humidity_2m}% RH
          </p>
          <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1.5">
            <Droplets size={12} /> {data.current.precipitation.toFixed(2)} in now
          </p>
        </>
      )}
    </Widget>
  );
}

function PressureCard({ data, loading, isFetching, updatedAt }: { data: any; loading?: boolean; isFetching?: boolean; updatedAt?: number }) {
  if (!data) return <Widget icon={Gauge} label="Barometer" loading={loading} isFetching={isFetching} source={OPEN_METEO_PROVENANCE} updatedAt={updatedAt}>{null}</Widget>;
  const trend = pressureTrendFromHourly(data.hourly);
  const Arrow = trend.trend === "rising" ? ArrowUp : trend.trend === "falling" ? ArrowDown : Minus;
  const color =
    trend.trend === "rising" ? "text-[var(--color-ember)]" :
    trend.trend === "falling" ? "text-destructive" : "text-muted-foreground";
  return (
    <Widget icon={Gauge} label="Barometer" loading={loading} isFetching={isFetching} source={OPEN_METEO_PROVENANCE} updatedAt={updatedAt}>
      <p className="font-display text-3xl sm:text-4xl">{data.current.pressure_msl.toFixed(0)}<span className="text-lg ml-1">hPa</span></p>
      <p className={`text-sm mt-1 flex items-center gap-1 ${color}`}>
        <Arrow size={14} /> {trend.trend} · {trend.delta >= 0 ? "+" : ""}{trend.delta.toFixed(1)} hPa / 3 hr
      </p>
      <p className="text-xs text-muted-foreground mt-2">
        {trend.trend === "falling"
          ? "Bite often picks up ahead of a front."
          : trend.trend === "rising"
          ? "Stable post-front. Halibut friendly."
          : "Settled. Pick spots by tide."}
      </p>
    </Widget>
  );
}

function PressureTrendCard({ data, loading, isFetching, updatedAt }: { data: any; loading?: boolean; isFetching?: boolean; updatedAt?: number }) {
  if (loading || !data?.hourly?.pressure_msl) {
    return (
      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="h-5 w-48 bg-muted/40 rounded animate-pulse" />
        <div className="mt-6 h-32 bg-muted/30 rounded animate-pulse" />
      </div>
    );
  }
  const times: string[] = data.hourly.time;
  const press: number[] = data.hourly.pressure_msl;
  const now = Date.now();
  let start = times.findIndex((t) => new Date(t).getTime() >= now);
  if (start < 0) start = 0;
  const slice = press.slice(start, start + 24);
  const timeSlice = times.slice(start, start + 24);
  if (slice.length < 2) return null;

  const current = slice[0];
  const end = slice[slice.length - 1];
  const delta = end - current;
  const min = Math.min(...slice);
  const max = Math.max(...slice);
  const range = Math.max(max - min, 1);

  const trend: "rising" | "falling" | "steady" =
    delta > 1.5 ? "rising" : delta < -1.5 ? "falling" : "steady";
  const Arrow = trend === "rising" ? ArrowUp : trend === "falling" ? ArrowDown : Minus;
  const trendColor =
    trend === "rising" ? "text-emerald-400" :
    trend === "falling" ? "text-amber-400" : "text-muted-foreground";
  const advice =
    trend === "falling"
      ? "Pressure dropping — feeding window. Fish often turn on 6–12 hr ahead of a front."
      : trend === "rising"
      ? "Pressure climbing — post-front stability. Halibut bite tends to steady up."
      : "Pressure steady — pick spots by tide phase and structure.";

  // Sparkline
  const W = 100;
  const H = 28;
  const points = slice.map((p, i) => {
    const x = (i / (slice.length - 1)) * W;
    const y = H - ((p - min) / range) * H;
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  });
  const linePath = `M ${points.join(" L ")}`;
  const areaPath = `${linePath} L ${W},${H} L 0,${H} Z`;

  const fmtHour = (iso: string) =>
    new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", timeZone: COFFMAN_COVE.timezone });

  return (
    <div className="rounded-2xl border border-border bg-card p-5 md:p-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Gauge size={16} className="text-[var(--color-ember)]" />
          <p className="uppercase tracking-[0.2em] text-[11px] font-semibold text-[var(--color-ember)]">
            Barometric Pressure — Next 24 Hours
          </p>
          <span className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
            <MapPin size={9} /> Coffman Cove, AK
          </span>
        </div>
        {isFetching && <Loader2 size={14} className="animate-spin text-muted-foreground" />}
      </div>

      <div className="mt-4 grid gap-6 md:grid-cols-[auto_1fr] items-center">
        <div>
          <p className="font-display text-4xl sm:text-5xl text-white">
            {current.toFixed(0)}
            <span className="text-lg ml-1 text-muted-foreground">hPa</span>
          </p>
          <div className={`mt-2 inline-flex items-center gap-2 rounded-full border border-border px-3 py-1 text-sm font-semibold ${trendColor}`}>
            <Arrow size={16} />
            <span className="capitalize">{trend}</span>
            <span className="opacity-80">
              {delta >= 0 ? "+" : ""}{delta.toFixed(1)} hPa / 24 hr
            </span>
          </div>
          <p className="mt-3 max-w-xs text-xs text-muted-foreground">{advice}</p>
        </div>

        <div>
          <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-32">
            <defs>
              <linearGradient id="pressFill" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="var(--color-ember)" stopOpacity="0.35" />
                <stop offset="100%" stopColor="var(--color-ember)" stopOpacity="0" />
              </linearGradient>
            </defs>
            <path d={areaPath} fill="url(#pressFill)" />
            <path d={linePath} fill="none" stroke="var(--color-ember)" strokeWidth="0.6" vectorEffect="non-scaling-stroke" />
          </svg>
          <div className="mt-1 flex justify-between text-[10px] text-muted-foreground font-mono">
            <span>Now · {fmtHour(timeSlice[0])}</span>
            <span>+12h · {fmtHour(timeSlice[Math.floor(slice.length / 2)])}</span>
            <span>+24h · {fmtHour(timeSlice[slice.length - 1])}</span>
          </div>
          <div className="mt-2 flex justify-between text-[10px] text-muted-foreground">
            <span>Low {min.toFixed(0)} hPa</span>
            <span>High {max.toFixed(0)} hPa</span>
          </div>
        </div>
      </div>

      <div className="mt-4 font-mono text-[10px] text-muted-foreground/80">
        {OPEN_METEO_PROVENANCE} · pressure_msl hourly · updated {formatRelative(updatedAt || Date.now())}
      </div>
    </div>
  );
}

function WindCard({ data, loading, isFetching, updatedAt }: { data: any; loading?: boolean; isFetching?: boolean; updatedAt?: number }) {
  if (!data) return <Widget icon={Wind} label="Wind" loading={loading} isFetching={isFetching} source={OPEN_METEO_PROVENANCE} updatedAt={updatedAt}>{null}</Widget>;
  const dir = data.current.wind_direction_10m;
  const bearing = compassBearing(dir);
  return (
    <Widget icon={Wind} label="Wind" loading={loading} isFetching={isFetching} source={OPEN_METEO_PROVENANCE} updatedAt={updatedAt}>
      <p className="font-display text-3xl sm:text-4xl">
        {Math.round(data.current.wind_speed_10m)}<span className="text-lg ml-1">kn</span>
      </p>
      <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5">
        <Compass size={14} /> from {bearing} ({Math.round(dir)}°)
      </p>
      <p className="text-xs text-muted-foreground mt-2">
        Gusts {Math.round(data.current.wind_gusts_10m)} kn
      </p>
    </Widget>
  );
}

function SeaCard({ data, weather, loading, isFetching, updatedAt }: { data: any; weather: any; loading?: boolean; isFetching?: boolean; updatedAt?: number }) {
  return (
    <Widget icon={Waves} label="Sea State" loading={loading} isFetching={isFetching} source={OPEN_METEO_MARINE_PROVENANCE} updatedAt={updatedAt}>

      {data?.current ? (
        <>
          <p className="font-display text-3xl sm:text-4xl">
            {data.current.wave_height?.toFixed(1) ?? "—"}<span className="text-lg ml-1">ft</span>
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            Period {data.current.wave_period?.toFixed(0) ?? "—"} s
          </p>
          {data.current.sea_surface_temperature != null && (
            <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1.5">
              <Eye size={12} /> SST {data.current.sea_surface_temperature.toFixed(0)}°F
            </p>
          )}
        </>
      ) : (
        <>
          <p className="font-display text-3xl sm:text-4xl">Sheltered</p>
          <p className="text-sm text-muted-foreground mt-1">
            Inside passage — open-coast swell not measured.
          </p>
          {weather?.current && (
            <p className="text-xs text-muted-foreground mt-2">
              Wind chop based on {Math.round(weather.current.wind_speed_10m)} kn.
            </p>
          )}
        </>
      )}
    </Widget>
  );
}

function TideChartCard({
  heights,
  extremes,
  loading,
  isFetching,
  updatedAt,
}: {
  heights?: TideHeight[];
  extremes?: TidePrediction[];
  loading?: boolean;
  isFetching?: boolean;
  updatedAt?: number;
}) {
  const now = Date.now();
  const windowEnd = now + 48 * 3600_000;
  const points = (heights ?? [])
    .map((h) => ({ t: new Date(h.t).getTime(), v: parseFloat(h.v) }))
    .filter((p) => Number.isFinite(p.v) && p.t >= now - 30 * 60_000 && p.t <= windowEnd)
    .sort((a, b) => a.t - b.t);

  const W = 1000;
  const H = 220;
  const PAD = { l: 40, r: 18, t: 22, b: 44 };
  const innerW = W - PAD.l - PAD.r;
  const innerH = H - PAD.t - PAD.b;

  let path = "";
  let area = "";
  let nowX = PAD.l;
  let minV = 0;
  let maxV = 0;
  let tMin = now;
  let tMax = windowEnd;
  const xTicks: { x: number; label: string }[] = [];
  const yTicks: { y: number; label: string }[] = [];
  const extremeDots: { x: number; y: number; type: "H" | "L"; label: string; v: number }[] = [];

  if (points.length > 1) {
    tMin = points[0].t;
    tMax = points[points.length - 1].t;
    const vMin = Math.min(...points.map((p) => p.v));
    const vMax = Math.max(...points.map((p) => p.v));
    const pad = Math.max(0.5, (vMax - vMin) * 0.1);
    minV = Math.floor(vMin - pad);
    maxV = Math.ceil(vMax + pad);

    const x = (t: number) => PAD.l + ((t - tMin) / (tMax - tMin)) * innerW;
    const y = (v: number) => PAD.t + (1 - (v - minV) / (maxV - minV)) * innerH;

    path = points.map((p, i) => `${i === 0 ? "M" : "L"}${x(p.t).toFixed(1)},${y(p.v).toFixed(1)}`).join(" ");
    area = `${path} L${x(points[points.length - 1].t).toFixed(1)},${(PAD.t + innerH).toFixed(1)} L${x(points[0].t).toFixed(1)},${(PAD.t + innerH).toFixed(1)} Z`;
    nowX = x(Math.max(tMin, Math.min(tMax, now)));

    // Y ticks (3)
    const yStep = (maxV - minV) / 3;
    for (let i = 0; i <= 3; i++) {
      const v = minV + yStep * i;
      yTicks.push({ y: y(v), label: `${v.toFixed(0)}` });
    }
    // X ticks every 12 hours, aligned to local midnight & noon — keeps
    // labels from overlapping at mobile viewport widths.
    const tz = COFFMAN_COVE.timezone;
    const start = new Date(tMin);
    const startHour = Number(
      new Intl.DateTimeFormat("en-US", { timeZone: tz, hour: "numeric", hour12: false }).format(start),
    );
    const firstTickMs = tMin + ((12 - (startHour % 12) + 12) % 12) * 3600_000;
    for (let tk = firstTickMs; tk <= tMax; tk += 12 * 3600_000) {
      const d = new Date(tk);
      const label = d.toLocaleTimeString("en-US", { timeZone: tz, hour: "numeric" });
      xTicks.push({ x: x(tk), label });
    }

    // Extreme markers within window
    (extremes ?? []).forEach((e) => {
      const et = new Date(e.t).getTime();
      if (et < tMin || et > tMax) return;
      const v = parseFloat(e.v);
      extremeDots.push({
        x: x(et),
        y: y(v),
        type: e.type,
        v,
        label: new Date(e.t).toLocaleTimeString("en-US", {
          timeZone: tz,
          hour: "numeric",
          minute: "2-digit",
        }),
      });
    });
  }

  return (
    <div className="rounded-xl bg-card border border-border p-5 shadow-sm relative overflow-hidden">
      <span className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-primary via-[var(--color-ember)] to-primary opacity-80" />
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 text-[var(--color-ember)] min-w-0">
          <Waves size={16} className="shrink-0" />
          <p className="uppercase tracking-[0.2em] text-[11px] font-semibold truncate">
            48-hour tide curve · Coffman Cove
          </p>
          {isFetching && !loading && (
            <Loader2 size={12} className="shrink-0 animate-spin opacity-80" aria-label="Refreshing" />
          )}
        </div>
        <span className="shrink-0 inline-flex items-center gap-1 rounded-full border border-border bg-background px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
          <MapPin size={9} /> Coffman Cove, AK
        </span>
      </div>

      {loading ? (
        <div className="h-[220px] rounded-lg bg-muted animate-pulse" aria-hidden />
      ) : points.length < 2 ? (
        <p className="text-sm text-muted-foreground py-10 text-center">Tide curve unavailable.</p>
      ) : (
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="chart-svg w-full h-auto"
          role="img"
          aria-label="Predicted tide height for the next 48 hours at Coffman Cove"
        >
          <defs>
            <linearGradient id="tideFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--color-primary)" stopOpacity="0.45" />
              <stop offset="100%" stopColor="var(--color-primary)" stopOpacity="0.02" />
            </linearGradient>
          </defs>

          {/* Y grid + labels */}
          {yTicks.map((tk, i) => (
            <g key={`y${i}`}>
              <line
                x1={PAD.l}
                x2={W - PAD.r}
                y1={tk.y}
                y2={tk.y}
                stroke="currentColor"
                strokeOpacity="0.12"
                strokeDasharray="2 4"
              />
              <text x={PAD.l - 6} y={tk.y + 4} textAnchor="end" className="fill-muted-foreground tick-sm">
                {tk.label} ft
              </text>
            </g>
          ))}

          {/* X labels */}
          {xTicks.map((tk, i) => (
            <text
              key={`x${i}`}
              x={tk.x}
              y={H - 8}
              textAnchor="middle"
              className="fill-muted-foreground tick-sm"
            >
              {tk.label}
            </text>
          ))}

          {/* Curve */}
          <path d={area} fill="url(#tideFill)" />
          <path d={path} fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinejoin="round" />

          {/* Now marker */}
          <line
            x1={nowX}
            x2={nowX}
            y1={PAD.t}
            y2={PAD.t + innerH}
            stroke="var(--color-ember)"
            strokeWidth="1.5"
            strokeDasharray="4 3"
          />
          <text x={nowX + 5} y={PAD.t + 12} className="fill-[var(--color-ember)] tick-strong">
            NOW
          </text>

          {/* Extreme markers — two-line labels (time / height) prevent
              horizontal overlap at narrow widths; circle color encodes H vs L. */}
          {extremeDots.map((d, i) => {
            const above = d.type === "H";
            const anchorY = above ? d.y - 10 : d.y + 16;
            return (
              <g key={`e${i}`}>
                <circle
                  cx={d.x}
                  cy={d.y}
                  r="4"
                  fill={above ? "var(--color-primary)" : "var(--color-ember)"}
                  stroke="var(--color-background)"
                  strokeWidth="1.5"
                />
                <text
                  x={d.x}
                  y={anchorY}
                  textAnchor="middle"
                  className="fill-foreground tick-strong"
                >
                  <tspan x={d.x} dy="0">{d.label}</tspan>
                  <tspan x={d.x} dy="1.15em" className="fill-muted-foreground">{d.v.toFixed(1)} ft</tspan>
                </text>
              </g>
            );
          })}
        </svg>
      )}

      <div className="mt-3 pt-3 border-t border-border/60 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] font-mono text-muted-foreground break-words">
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-3 h-[2px] bg-primary" /> Tide height
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-full bg-primary" /> High
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-full bg-[var(--color-ember)]" /> Low
        </span>
        {updatedAt ? (
          <span className="ml-auto text-[10px] uppercase tracking-[0.18em] text-[var(--color-ember)]">
            Updated {formatRelative(updatedAt)}
          </span>
        ) : null}
      </div>
      <p className="mt-1 text-[11px] font-mono text-muted-foreground break-words leading-snug">
        NOAA CO-OPS station 9450906 · Beck Island · 30-min predictions · MLLW · {COFFMAN_COVE.timezone}
      </p>
    </div>
  );
}

function TideStatusCard({ tides, loading, isFetching, updatedAt }: { tides?: TidePrediction[]; loading?: boolean; isFetching?: boolean; updatedAt?: number }) {

  const now = Date.now();
  const upcoming = (tides ?? [])
    .map((t) => ({ ...t, time: new Date(t.t).getTime() }))
    .filter((t) => t.time >= now)
    .sort((a, b) => a.time - b.time);
  const nextHigh = upcoming.find((t) => t.type === "H");
  const nextLow = upcoming.find((t) => t.type === "L");

  const fmtTime = (iso: string) =>
    new Date(iso).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      timeZone: COFFMAN_COVE.timezone,
    });
  const fmtDay = (iso: string) => {
    const d = new Date(iso);
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);
    if (d.toDateString() === today.toDateString()) return "Today";
    if (d.toDateString() === tomorrow.toDateString()) return "Tomorrow";
    return d.toLocaleDateString("en-US", { weekday: "short", timeZone: COFFMAN_COVE.timezone });
  };
  const countdown = (ms: number) => {
    const mins = Math.max(0, Math.round((ms - now) / 60000));
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h === 0) return `in ${m}m`;
    return `in ${h}h ${m.toString().padStart(2, "0")}m`;
  };

  return (
    <div className="rounded-xl bg-gradient-to-br from-[var(--color-navy)] via-card to-card border border-border p-5 shadow-sm relative overflow-hidden">
      <span className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-primary via-[var(--color-ember)] to-primary opacity-80" />
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2 text-[var(--color-ember)] min-w-0">
          <Anchor size={16} className="shrink-0" />
          <p className="uppercase tracking-[0.2em] text-[11px] font-semibold truncate">Next tide · Coffman Cove</p>
          {isFetching && !loading && <Loader2 size={12} className="shrink-0 animate-spin opacity-80" aria-label="Refreshing" />}
        </div>
        <span className="shrink-0 inline-flex items-center gap-1 rounded-full border border-border bg-background px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
          <MapPin size={9} /> Coffman Cove, AK
        </span>
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 gap-4" aria-hidden>
          <div className="h-24 rounded-lg bg-muted animate-pulse" />
          <div className="h-24 rounded-lg bg-muted animate-pulse" />
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          <TideStatusTile
            label="Next High Tide"
            icon={<ArrowUp size={18} />}
            tone="high"
            tide={nextHigh}
            fmtTime={fmtTime}
            fmtDay={fmtDay}
            countdown={countdown}
          />
          <TideStatusTile
            label="Next Low Tide"
            icon={<ArrowDown size={18} />}
            tone="low"
            tide={nextLow}
            fmtTime={fmtTime}
            fmtDay={fmtDay}
            countdown={countdown}
          />
        </div>
      )}

      <div className="mt-4 pt-3 border-t border-border/60">
        {updatedAt ? (
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--color-ember)]">
            Updated {formatRelative(updatedAt)}
          </p>
        ) : null}
        <p className="text-[11px] font-mono text-muted-foreground break-words leading-snug mt-1">
          NOAA CO-OPS station 9450906 · Beck Island · MLLW · {COFFMAN_COVE.timezone}
        </p>
      </div>
    </div>
  );
}

function TideStatusTile({
  label,
  icon,
  tone,
  tide,
  fmtTime,
  fmtDay,
  countdown,
}: {
  label: string;
  icon: React.ReactNode;
  tone: "high" | "low";
  tide?: TidePrediction & { time: number };
  fmtTime: (iso: string) => string;
  fmtDay: (iso: string) => string;
  countdown: (ms: number) => string;
}) {
  const accent =
    tone === "high"
      ? "from-primary/20 to-transparent border-primary/40 text-primary"
      : "from-[var(--color-ember)]/15 to-transparent border-[var(--color-ember)]/40 text-[var(--color-ember)]";
  return (
    <div className={`rounded-lg border bg-gradient-to-br ${accent} p-4`}>
      <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em]">
        {icon}
        <span>{label}</span>
      </div>
      {tide ? (
        <div className="mt-2">
          <p className="font-display text-2xl sm:text-3xl text-foreground leading-none">{fmtTime(tide.t)}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {fmtDay(tide.t)} · {countdown(tide.time)} · {parseFloat(tide.v).toFixed(1)} ft
          </p>
        </div>
      ) : (
        <p className="mt-2 text-sm text-muted-foreground">No upcoming prediction.</p>
      )}
    </div>
  );
}


function TideCard({ tides, heights, loading, isFetching, updatedAt }: { tides?: TidePrediction[]; heights?: TideHeight[]; loading?: boolean; isFetching?: boolean; updatedAt?: number }) {
  const today = (tides ?? []).filter((t) => {

    const d = new Date(t.t);
    const now = new Date();
    return d.toDateString() === now.toDateString();
  });
  const tomorrow = (tides ?? []).filter((t) => {
    const d = new Date(t.t);
    const t1 = new Date();
    t1.setDate(t1.getDate() + 1);
    return d.toDateString() === t1.toDateString();
  });

  return (
    <div className="rounded-xl bg-card border border-border p-5 shadow-sm h-full relative overflow-hidden">
      <span className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-primary via-[var(--color-ember)] to-primary opacity-80" />
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[var(--color-ember)] min-w-0">
          <Anchor size={16} className="shrink-0" />
          <p className="uppercase tracking-[0.2em] text-[11px] font-semibold truncate">Tides — Coffman Cove area</p>
          {isFetching && !loading && <Loader2 size={12} className="shrink-0 animate-spin opacity-80" aria-label="Refreshing" />}
        </div>
        <span className="shrink-0 inline-flex items-center gap-1 rounded-full border border-border bg-background px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
          <MapPin size={9} /> Coffman Cove, AK
        </span>
      </div>
      {loading ? (
        <div className="mt-4 space-y-2" aria-hidden>
          <div className="h-4 w-1/2 rounded bg-muted animate-pulse" />
          <div className="h-4 w-2/3 rounded bg-muted animate-pulse" />
          <div className="h-4 w-1/3 rounded bg-muted animate-pulse" />
          <div className="h-4 w-1/2 rounded bg-muted animate-pulse" />
        </div>
      ) : (
        <div className="mt-4 grid sm:grid-cols-2 gap-6">
          <TideColumn label="Today" rows={today} />
          <TideColumn label="Tomorrow" rows={tomorrow} />
        </div>
      )}
      {!loading && heights && heights.length > 1 && <TideMiniTimeline heights={heights} />}

      <div className="mt-4 pt-3 border-t border-border/60 space-y-1">
        {updatedAt ? (
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--color-ember)]">
            Updated {formatRelative(updatedAt)}
          </p>
        ) : null}
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--color-ember)]">
          Data source
        </p>
        <p className="text-[11px] font-mono text-muted-foreground break-words leading-snug">
          NOAA CO-OPS station <span className="font-semibold">9450906 · Beck Island, Clarence Strait, AK</span> —
          the closest official tide gauge to Coffman Cove, sitting roughly 0.6 mi offshore from the
          village per NOAA's published benchmark sheet. Predictions are MLLW datum, hi/lo interval,
          served in {COFFMAN_COVE.timezone} (LST/LDT) and used as the reference tide curve for the
          Coffman Cove dock.

        </p>
      </div>
    </div>
  );
}

function TideMiniTimeline({ heights }: { heights: TideHeight[] }) {
  const now = Date.now();
  const horizon = now + 24 * 3600 * 1000;
  const points = heights
    .map((h) => ({ t: new Date(h.t).getTime(), v: parseFloat(h.v) }))
    .filter((p) => Number.isFinite(p.v) && p.t >= now - 60 * 60 * 1000 && p.t <= horizon)
    .sort((a, b) => a.t - b.t);
  if (points.length < 2) return null;

  const W = 600, H = 100, PAD_X = 10, PAD_Y = 18;
  const tMin = points[0].t, tMax = points[points.length - 1].t;
  const vs = points.map((p) => p.v);
  const vMin = Math.min(...vs), vMax = Math.max(...vs);
  const vRange = vMax - vMin || 1;
  const x = (t: number) => PAD_X + ((t - tMin) / (tMax - tMin)) * (W - PAD_X * 2);
  const y = (v: number) => PAD_Y + (1 - (v - vMin) / vRange) * (H - PAD_Y * 2);

  const path = points.map((p, i) => `${i === 0 ? "M" : "L"}${x(p.t).toFixed(1)},${y(p.v).toFixed(1)}`).join(" ");
  const area = `${path} L${x(points[points.length - 1].t).toFixed(1)},${H - PAD_Y} L${x(points[0].t).toFixed(1)},${H - PAD_Y} Z`;
  const nowX = x(Math.max(tMin, Math.min(tMax, now)));

  // Determine rising/falling at "now"
  const nearestIdx = points.findIndex((p) => p.t >= now);
  const i1 = Math.max(1, nearestIdx === -1 ? points.length - 1 : nearestIdx);
  const rising = points[i1].v > points[i1 - 1].v;

  const ticks = [6, 12, 18].map((h) => {
    const t = now + h * 3600 * 1000;
    return { t, label: `+${h}h` };
  }).filter((tk) => tk.t <= tMax);

  return (
    <div className="mt-5 pt-4 border-t border-border/60">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--color-ember)]">
          Next 24h timeline
        </p>
        <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider ${rising ? "text-[var(--color-ember)]" : "text-primary/70"}`}>
          {rising ? <ArrowUp size={11} /> : <ArrowDown size={11} />}
          {rising ? "Rising" : "Falling"}
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="chart-svg w-full h-20" role="img" aria-label="Mini tide timeline next 24 hours">
        <defs>
          <linearGradient id="tideMiniFill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.35" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
          </linearGradient>
        </defs>
        <g className="text-primary">
          <path d={area} fill="url(#tideMiniFill)" />
          <path d={path} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round" strokeLinecap="round" />
        </g>
        <line x1={nowX} x2={nowX} y1={PAD_Y - 4} y2={H - PAD_Y + 4} stroke="var(--color-ember)" strokeWidth="1" strokeDasharray="3 3" />
        <text x={nowX + 4} y={PAD_Y - 2} className="fill-[var(--color-ember)] tick-strong font-mono">NOW</text>
        {ticks.map((tk) => (
          <text key={tk.label} x={x(tk.t)} y={H - 2} textAnchor="middle" className="fill-muted-foreground tick-sm font-mono">
            {tk.label}
          </text>
        ))}
        <text x={PAD_X} y={PAD_Y - 4} className="fill-muted-foreground tick-sm font-mono">{vMax.toFixed(1)}ft</text>
        <text x={PAD_X} y={H - PAD_Y + 10} className="fill-muted-foreground tick-sm font-mono">{vMin.toFixed(1)}ft</text>
      </svg>
    </div>
  );
}



function TideColumn({ label, rows }: { label: string; rows: TidePrediction[] }) {
  return (
    <div>
      <p className="font-display text-lg mb-2">{label}</p>
      <ul className="space-y-2">
        {rows.length === 0 && <li className="text-sm text-muted-foreground">No data.</li>}
        {rows.map((r) => (
          <li key={r.t} className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2 text-primary">
              {r.type === "H" ? (
                <ArrowUp size={14} className="text-[var(--color-ember)]" />
              ) : (
                <ArrowDown size={14} className="text-primary/60" />
              )}
              {r.type === "H" ? "High" : "Low"}
            </span>
            <span className="font-mono text-muted-foreground break-words">
              {new Date(r.t).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
            </span>
            <span className="font-mono font-semibold">{parseFloat(r.v).toFixed(1)} ft</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SunMoonCard({ data, loading, isFetching, updatedAt }: { data: any; loading?: boolean; isFetching?: boolean; updatedAt?: number }) {
  const moon = moonPhase();
  return (
    <div className="rounded-xl bg-card border border-border p-5 shadow-sm h-full relative overflow-hidden">
      <span className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-primary via-[var(--color-ember)] to-primary opacity-80" />
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[var(--color-ember)] min-w-0">
          <Moon size={16} className="shrink-0" />
          <p className="uppercase tracking-[0.2em] text-[11px] font-semibold truncate">Sun & Moon</p>
          {isFetching && !loading && <Loader2 size={12} className="shrink-0 animate-spin opacity-80" aria-label="Refreshing" />}
        </div>
        <span className="shrink-0 inline-flex items-center gap-1 rounded-full border border-border bg-background px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
          <MapPin size={9} /> Coffman Cove, AK
        </span>
      </div>
      {loading || !data ? (
        <div className="mt-4 space-y-2" aria-hidden>
          <div className="h-4 w-1/2 rounded bg-muted animate-pulse" />
          <div className="h-4 w-2/3 rounded bg-muted animate-pulse" />
          <div className="h-4 w-1/3 rounded bg-muted animate-pulse" />
          <div className="h-4 w-1/2 rounded bg-muted animate-pulse" />
        </div>
      ) : (
        <div className="mt-4 space-y-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2"><Sunrise size={14} /> Sunrise</span>
            <span className="font-mono">
              {new Date(data.daily.sunrise[0]).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2"><Sunset size={14} /> Sunset</span>
            <span className="font-mono">
              {new Date(data.daily.sunset[0]).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
            </span>
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-border">
            <span className="flex items-center gap-2"><Moon size={14} /> Moon</span>
            <span className="font-mono">{moonPhaseName(moon)}</span>
          </div>
          <p className="text-xs text-muted-foreground">
            {moon < 0.06 || moon > 0.94 || Math.abs(moon - 0.5) < 0.06
              ? "Spring tides — strong currents, peak feeding windows."
              : "Neap tides — slower water, focus on bait concentrations."}
          </p>
        </div>
      )}
      <div className="mt-4 pt-3 border-t border-border/60 space-y-1">
        {updatedAt ? (
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--color-ember)]">
            Updated {formatRelative(updatedAt)}
          </p>
        ) : null}
        <p className="text-[10px] font-mono text-muted-foreground break-words leading-tight">
          {OPEN_METEO_PROVENANCE} · moon phase computed locally
        </p>
      </div>
    </div>
  );
}


function ScoreBadge({ score }: { score: number }) {
  const color =
    score >= 75 ? "bg-primary text-primary-foreground" :
    score >= 55 ? "bg-[var(--color-ember)] text-[var(--color-ember-foreground)]" :
    "bg-muted text-muted-foreground";
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-bold tracking-wide ${color}`}>
      {score}
    </span>
  );
}

function compassBearing(deg: number) {
  const dirs = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  return dirs[Math.round(deg / 45) % 8];
}

// Preset depth bands (values stored internally in feet). Labels are derived
// per-unit so a ft ↔ m toggle changes only presentation, never the filter math.
export const DEPTH_FILTERS = [
  { id: "all", ftRange: [0, 10000] as [number, number] },
  { id: "shoal", ftRange: [0, 100] as [number, number] },
  { id: "mid", ftRange: [100, 200] as [number, number] },
  { id: "deep", ftRange: [200, 10000] as [number, number] },
] as const;
export type DepthFilterId = (typeof DEPTH_FILTERS)[number]["id"];
export type DepthUnit = "ft" | "m";

const FT_PER_M = 3.28084;
export function formatDepthBand(id: DepthFilterId, unit: DepthUnit): string {
  const fmt = (ft: number) => (unit === "ft" ? `${Math.round(ft)} ft` : `${Math.round(ft / FT_PER_M)} m`);
  switch (id) {
    case "all": return "All depths";
    case "shoal": return `Shoal · <${fmt(100)}`;
    case "mid": return `Mid · ${fmt(100)}–${fmt(200)}`;
    case "deep": return `Deep · >${fmt(200)}`;
  }
}

export function depthOverlaps(spotRange: [number, number], filterRange: [number, number]) {
  return spotRange[0] <= filterRange[1] && spotRange[1] >= filterRange[0];
}


function SpotFinder({
  recs,
  target,
  setTarget,
  loading,
}: {
  recs: WindowRecommendation[];
  target: "king" | "halibut";
  setTarget: (t: "king" | "halibut") => void;
  loading?: boolean;
}) {
  const [depthFilter, setDepthFilter] = useState<DepthFilterId>("all");
  const [depthUnit, setDepthUnit] = useState<DepthUnit>("ft");
  const activeRange = DEPTH_FILTERS.find((f) => f.id === depthFilter)!.ftRange;

  return (
    <div className="rounded-xl border border-[var(--color-ember)]/30 bg-gradient-to-br from-card to-[var(--color-deep)]/40 p-6 md:p-10 shadow-lg">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="uppercase tracking-[0.3em] text-xs text-[var(--color-ember)] flex items-center gap-2">
            <Target size={14} /> Spot Finder · Today's tide windows
          </p>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl mt-2 text-balance text-primary">
            Where we suggest you should fish today.
          </h2>

          <p className="mt-2 text-sm text-muted-foreground max-w-prose">
            For each upcoming high-tide window, we match the forecast wind, pressure trend and
            tide phase against spots within 25 mi of the dock and surface the top two.
          </p>
        </div>
        <div className="flex gap-1 rounded-md border border-border bg-background p-1">
          {(["king", "halibut"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTarget(t)}
              className={`px-4 py-3 min-h-11 text-sm font-semibold uppercase tracking-wider rounded ${
                target === t
                  ? "bg-[var(--color-ember)] text-[var(--color-ember-foreground)]"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t === "king" ? "King Salmon" : "Halibut"}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <span className="text-[11px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
          <Waves size={12} className="text-[var(--color-electric)]" /> Depth
        </span>
        {DEPTH_FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setDepthFilter(f.id)}
            aria-pressed={depthFilter === f.id}
            className={`px-3 py-1.5 min-h-9 text-xs font-semibold rounded-full border transition ${
              depthFilter === f.id
                ? "bg-[var(--color-electric)] text-[var(--color-ember-foreground)] border-[var(--color-electric)]"
                : "border-border text-muted-foreground hover:text-foreground hover:border-[var(--color-electric)]/60"
            }`}
          >
            {formatDepthBand(f.id, depthUnit)}
          </button>
        ))}
        <div className="ml-1 flex gap-0.5 rounded-md border border-border bg-background p-0.5" role="group" aria-label="Depth unit">
          {(["ft", "m"] as const).map((u) => (
            <button
              key={u}
              type="button"
              onClick={() => setDepthUnit(u)}
              aria-pressed={depthUnit === u}
              className={`px-2.5 py-1 min-h-8 text-[11px] font-semibold uppercase rounded ${
                depthUnit === u
                  ? "bg-[var(--color-electric)] text-[var(--color-ember-foreground)]"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {u}
            </button>
          ))}
        </div>
      </div>


      <div className="mt-8 grid gap-5 lg:grid-cols-3">
        {loading && (
          <p className="text-muted-foreground col-span-full">Loading tide windows…</p>
        )}
        {!loading && recs.length === 0 && (
          <p className="text-muted-foreground col-span-full">
            No upcoming high-tide windows in the forecast range.
          </p>
        )}
        {recs.map((rec, i) => {
          const picks = rec.picks.filter((p) => depthOverlaps(p.spot.depthFt, activeRange));
          const ht = rec.window.highTide;

          const time = new Date(ht.t);
          const dayLabel =
            time.toDateString() === new Date().toDateString()
              ? "Today"
              : time.toLocaleDateString([], { weekday: "short" });
          const fc = rec.window.forecast;
          return (
            <article
              key={ht.t}
              className="rounded-lg border border-border bg-background/80 backdrop-blur p-5 flex flex-col"
            >
              <header className="flex items-baseline justify-between">
                <span className="text-xs uppercase tracking-widest text-[var(--color-ember)] font-semibold">
                  Window {i + 1}
                </span>
                <span className="text-xs text-muted-foreground">
                  {parseFloat(ht.v).toFixed(1)} ft high
                </span>
              </header>
              <p className="font-display text-2xl mt-1 flex items-center gap-2 text-primary">
                <Clock size={18} className="text-[var(--color-ember)]" />
                {dayLabel}{" "}
                {time.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Best 90 min before → 90 min after peak
              </p>

              <div className="mt-3 grid grid-cols-3 gap-2 text-[11px] text-muted-foreground border-y border-border py-3">
                <span className="flex items-center gap-1">
                  <Wind size={11} /> {fc.windKn.toFixed(0)} kn {compassBearing(fc.windDir)}
                </span>
                <span className="flex items-center gap-1">
                  <Gauge size={11} /> {fc.pressureMb.toFixed(0)} hPa {fc.pressureTrend === "rising" ? "↑" : fc.pressureTrend === "falling" ? "↓" : "→"}
                </span>
                <span className="flex items-center gap-1">
                  <CloudRain size={11} /> {fc.precipProb}%
                </span>
              </div>

              <ul className="mt-4 space-y-3">
                {picks.length === 0 && (
                  <li className="text-xs text-muted-foreground italic py-2">
                    No spots in this depth band for this window.
                  </li>
                )}
                {picks.map((p, idx) => (
                  <li key={p.spot.id}>
                    <button
                      type="button"
                      onClick={() => {
                        window.dispatchEvent(
                          new CustomEvent("ccl:selectSpot", { detail: { id: p.spot.id } }),
                        );
                        document
                          .getElementById("spot-map")
                          ?.scrollIntoView({ behavior: "smooth", block: "start" });
                      }}
                      className="w-full text-left flex gap-3 rounded-md p-2 -mx-2 hover:bg-[var(--color-ember)]/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ember)] transition"
                      aria-label={`Zoom map to ${p.spot.name}`}
                    >
                      <span className="font-display text-lg text-[var(--color-ember)] w-5 shrink-0">
                        {idx + 1}.
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-semibold text-primary truncate flex items-center gap-1">
                            <MapPin size={12} className="text-[var(--color-ember)] shrink-0" />
                            {p.spot.name}
                          </p>
                          <ScoreBadge score={p.score} />
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                          <span>{p.spot.bearing} · {p.spot.distanceMi} mi</span>
                          {p.spot.historicalCatch?.[target] != null && (
                            <span className="inline-flex items-center gap-1 rounded-sm bg-[var(--color-ember)]/15 text-[var(--color-ember)] px-1.5 py-0.5 font-semibold">
                              Hist {p.spot.historicalCatch[target]}/100
                            </span>
                          )}
                        </p>
                        {(() => {
                          const [lo, hi] = p.spot.depthFt;
                          const nearestFt = Math.round(lo / 5) * 5;
                          const toUnit = (ft: number) =>
                            depthUnit === "ft" ? `${Math.round(ft)}` : `${Math.round(ft / FT_PER_M)}`;
                          return (
                            <p className="text-[11px] text-primary/90 mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                              <span className="inline-flex items-center gap-1 rounded-sm border border-[var(--color-electric)]/40 bg-[var(--color-electric)]/10 px-1.5 py-0.5 font-mono">
                                <Waves size={11} className="text-[var(--color-electric)]" />
                                ~{toUnit(nearestFt)} {depthUnit} nearest
                              </span>
                              <span className="text-muted-foreground font-mono">
                                contour {toUnit(lo)}–{toUnit(hi)} {depthUnit}
                              </span>
                            </p>
                          );
                        })()}


                        {p.reasons.slice(0, 2).map((r, ri) => (
                          <p key={ri} className="text-xs text-muted-foreground mt-1 flex gap-1.5">
                            <Fish size={11} className="mt-0.5 shrink-0 text-[var(--color-ember)]" />
                            <span>{r}</span>
                          </p>
                        ))}
                        <p className="text-[10px] uppercase tracking-wider text-[var(--color-ember)]/80 mt-1 font-semibold">
                          Tap to zoom map →
                        </p>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>

            </article>
          );
        })}
      </div>
    </div>
  );
}

function weatherCodeInfo(code: number): { label: string; icon: React.ComponentType<{ size?: number; className?: string }> } {
  if (code === 0) return { label: "Clear", icon: Sunrise };
  if (code <= 2) return { label: "Mostly clear", icon: Sunrise };
  if (code === 3) return { label: "Overcast", icon: Eye };
  if (code <= 48) return { label: "Fog", icon: Eye };
  if (code <= 57) return { label: "Drizzle", icon: CloudRain };
  if (code <= 67) return { label: "Rain", icon: CloudRain };
  if (code <= 77) return { label: "Snow", icon: CloudRain };
  if (code <= 82) return { label: "Showers", icon: CloudRain };
  if (code <= 86) return { label: "Snow showers", icon: CloudRain };
  return { label: "Storm", icon: CloudRain };
}

function SevenDayForecast({
  data,
  loading,
  error,
  isFetching,
  onRetry,
}: {
  data: any;
  loading?: boolean;
  error?: Error | null;
  isFetching?: boolean;
  onRetry?: () => void;
}) {
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [windUnit, setWindUnit] = useState<"kn" | "mph">(() => {
    if (typeof window === "undefined") return "kn";
    const v = window.localStorage.getItem("cc.windUnit");
    return v === "mph" || v === "kn" ? v : "kn";
  });
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem("cc.windUnit", windUnit);
    }
  }, [windUnit]);
  const hasError = !!error || (!loading && !data?.daily);
  const convertWind = (kn: number) => (windUnit === "mph" ? kn * 1.15078 : kn);


  return (
    <div className="rounded-xl bg-card border border-border p-5 md:p-6 shadow-sm relative overflow-hidden">
      <span className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-primary via-[var(--color-ember)] to-primary opacity-80" />
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[var(--color-ember)]">
          <Thermometer size={16} />
          <p className="uppercase tracking-[0.2em] text-[11px] font-semibold">7-Day Forecast</p>
        </div>
        <div className="flex items-center gap-3">
          <div
            role="group"
            aria-label="Wind unit"
            className="inline-flex rounded-md border border-border bg-background p-0.5 text-[10px] font-semibold uppercase tracking-wider"
          >
            {(["kn", "mph"] as const).map((u) => (
              <button
                key={u}
                type="button"
                onClick={() => setWindUnit(u)}
                aria-pressed={windUnit === u}
                className={`inline-flex items-center justify-center min-h-9 min-w-9 px-3 py-1.5 rounded transition-colors ${
                  windUnit === u
                    ? "bg-[var(--color-ember)] text-[var(--color-ember-foreground)]"
                    : "text-muted-foreground hover:text-primary"
                }`}
              >
                {u}
              </button>
            ))}
          </div>
          <p className="text-[10px] sm:text-xs text-muted-foreground text-right leading-tight hidden md:block">
            <span className="hidden lg:inline">Tap a day for hourly · </span>
            Open-Meteo · {COFFMAN_COVE.lat.toFixed(4)}°N, {Math.abs(COFFMAN_COVE.lon).toFixed(4)}°W
          </p>
        </div>

      </div>

      {loading ? (
        <div
          className="mt-5 -mx-4 md:-mx-6 lg:mx-0 px-4 md:px-6 lg:px-0 pb-2 lg:pb-0 flex lg:grid lg:grid-cols-7 gap-3 overflow-x-auto lg:overflow-visible snap-x snap-mandatory overscroll-x-contain scroll-pl-4 md:scroll-pl-6 lg:scroll-pl-0 scroll-smooth scrollbar-thin [-webkit-overflow-scrolling:touch] [scroll-padding-inline-end:1.25rem]"
          aria-busy="true"
          aria-label="Loading 7-day forecast"
        >
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={i} className="snap-start snap-always shrink-0 basis-[45%] sm:basis-[30%] lg:basis-auto lg:w-auto rounded-lg border border-border bg-background p-3 flex flex-col items-center gap-2">

              <div className="h-3 w-10 rounded bg-muted animate-pulse" />
              <div className="h-2 w-12 rounded bg-muted animate-pulse" />
              <div className="h-7 w-7 rounded-full bg-muted animate-pulse my-1" />
              <div className="h-5 w-14 rounded bg-muted animate-pulse" />
              <div className="h-2 w-16 rounded bg-muted animate-pulse" />
              <div className="mt-2 pt-2 border-t border-border/60 w-full space-y-1">
                <div className="h-2 w-12 mx-auto rounded bg-muted animate-pulse" />
                <div className="h-2 w-10 mx-auto rounded bg-muted animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      ) : hasError ? (
        <div className="mt-5 rounded-lg border border-destructive/30 bg-destructive/5 p-6 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="shrink-0 h-12 w-12 rounded-full bg-destructive/15 flex items-center justify-center text-destructive">
            <AlertTriangle size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-display text-lg text-primary">Forecast unavailable</p>
            <p className="text-sm text-muted-foreground mt-1">
              {error?.message
                ? `We couldn't reach the Open-Meteo forecast service (${error.message}).`
                : "We couldn't reach the Open-Meteo forecast service right now."}{" "}
              Check your connection and try again — tides and other live widgets are unaffected.
            </p>
          </div>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              disabled={isFetching}
              className="inline-flex items-center gap-2 rounded-md bg-[var(--color-ember)] text-[var(--color-ember-foreground)] px-4 py-2 text-sm font-semibold uppercase tracking-wider hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed transition"
            >
              <RefreshCw size={14} className={isFetching ? "animate-spin" : ""} />
              {isFetching ? "Retrying…" : "Retry"}
            </button>
          )}
        </div>
      ) : (
        <div
          role="tablist"
          aria-label="7-day forecast — tap a day for hourly details"
          className="mt-5 -mx-4 md:-mx-6 lg:mx-0 px-4 md:px-6 lg:px-0 pb-2 lg:pb-0 flex lg:grid lg:grid-cols-7 gap-3 overflow-x-auto lg:overflow-visible snap-x snap-mandatory overscroll-x-contain scroll-pl-4 md:scroll-pl-6 lg:scroll-pl-0 scroll-smooth scrollbar-thin [-webkit-overflow-scrolling:touch] [scroll-padding-inline-end:1.25rem]"
        >
          {data.daily.time.map((iso: string, i: number) => {
            const d = new Date(iso);
            const info = weatherCodeInfo(data.daily.weather_code[i]);
            const Icon = info.icon;
            const isToday = d.toDateString() === new Date().toDateString();
            const dayLabel = isToday ? "Today" : d.toLocaleDateString([], { weekday: "long" });
            const dateLabel = d.toLocaleDateString([], { month: "long", day: "numeric" });
            const hi = Math.round(data.daily.temperature_2m_max[i]);
            const lo = Math.round(data.daily.temperature_2m_min[i]);
            return (
              <button
                key={iso}
                type="button"
                role="tab"
                aria-label={`${dayLabel}, ${dateLabel}. ${info.label}. High ${hi}°, low ${lo}°. Tap for hourly breakdown.`}
                onClick={() => setSelectedDay(iso)}
                onKeyDown={(e) => {
                  if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
                    e.preventDefault();
                    const dir = e.key === "ArrowRight" ? 1 : -1;
                    const next = (e.currentTarget.parentElement?.children[i + dir] as HTMLElement | undefined);
                    next?.focus();
                    next?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
                  }
                }}
                className={`snap-start snap-always shrink-0 basis-[45%] sm:basis-[30%] lg:basis-auto lg:w-auto min-h-[11rem] touch-manipulation select-none rounded-lg border p-3 flex flex-col items-center text-center transition-all cursor-pointer hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ember)] focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-[0.98] ${
                  isToday
                    ? "border-[var(--color-ember)] bg-[var(--color-ember)]/5"
                    : "border-border bg-background hover:border-[var(--color-ember)]/50"
                }`}
              >

                <p className="text-[11px] uppercase tracking-widest text-muted-foreground">
                  {isToday ? "Today" : d.toLocaleDateString([], { weekday: "short" })}
                </p>
                <p className="text-[10px] text-muted-foreground">
                  {d.toLocaleDateString([], { month: "short", day: "numeric" })}
                </p>
                <Icon size={26} className="my-2 text-[var(--color-ember)]" />
                <p className="font-display text-lg text-primary leading-none">
                  {Math.round(data.daily.temperature_2m_max[i])}°
                  <span className="text-muted-foreground text-sm ml-1">
                    {Math.round(data.daily.temperature_2m_min[i])}°
                  </span>
                </p>
                <p className="text-[10px] text-muted-foreground mt-1 line-clamp-1">{info.label}</p>
                <div className="mt-2 pt-2 border-t border-border/60 w-full space-y-0.5 text-[10px] text-muted-foreground">
                  <p className="flex items-center justify-center gap-1">
                    <Wind size={10} /> {Math.round(convertWind(data.daily.wind_speed_10m_max[i]))} {windUnit}
                  </p>
                  <p className="flex items-center justify-center gap-1">
                    <CloudRain size={10} /> {data.daily.precipitation_probability_max?.[i] ?? 0}%
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      )}


      <HourlyDayDialog
        dayIso={selectedDay}
        data={data}
        windUnit={windUnit}
        convertWind={convertWind}
        onClose={() => setSelectedDay(null)}
      />
    </div>
  );
}

function HourlyDayDialog({
  dayIso,
  data,
  windUnit,
  convertWind,
  onClose,
}: {
  dayIso: string | null;
  data: any;
  windUnit: "kn" | "mph";
  convertWind: (kn: number) => number;
  onClose: () => void;
}) {
  const hours = useMemo(() => {
    if (!dayIso || !data?.hourly?.time) return [];
    const target = new Date(dayIso).toDateString();
    return data.hourly.time
      .map((t: string, i: number) => ({ t, i }))
      .filter(({ t }: { t: string }) => new Date(t).toDateString() === target);
  }, [dayIso, data]);

  const dayLabel = dayIso
    ? new Date(dayIso).toLocaleDateString([], {
        weekday: "long",
        month: "long",
        day: "numeric",
      })
    : "";

  return (
    <Dialog open={!!dayIso} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{dayLabel}</DialogTitle>
          <DialogDescription>
            Hourly forecast for Coffman Cove — temperature, wind, pressure and precipitation.
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-y-auto -mx-6 px-6">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-background z-10">
              <tr className="text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="text-left py-2 font-semibold">Time</th>
                <th className="text-right py-2 font-semibold">Temp</th>
                <th className="text-right py-2 font-semibold">Wind</th>
                <th className="text-right py-2 font-semibold">Pressure</th>
                <th className="text-right py-2 font-semibold">Rain</th>
              </tr>
            </thead>
            <tbody>
              {hours.map(({ t, i }: { t: string; i: number }) => {
                const time = new Date(t);
                const isPast = time.getTime() < Date.now() - 30 * 60_000;
                return (
                  <tr
                    key={t}
                    className={`border-b border-border/50 ${isPast ? "opacity-50" : ""}`}
                  >
                    <td className="py-2 font-mono text-xs">
                      {time.toLocaleTimeString([], { hour: "numeric" })}
                    </td>
                    <td className="py-2 text-right text-primary font-semibold">
                      {Math.round(data.hourly.temperature_2m[i])}°F
                    </td>
                    <td className="py-2 text-right text-muted-foreground">
                      {Math.round(convertWind(data.hourly.wind_speed_10m[i]))} {windUnit}{" "}
                      <span className="text-[10px]">
                        {compassBearing(data.hourly.wind_direction_10m[i])}
                      </span>
                    </td>
                    <td className="py-2 text-right text-muted-foreground font-mono text-xs">
                      {data.hourly.pressure_msl[i]?.toFixed(0)} hPa
                    </td>
                    <td className="py-2 text-right text-muted-foreground">
                      <span className="inline-flex items-center gap-1">
                        <CloudRain size={11} className="text-[var(--color-ember)]" />
                        {data.hourly.precipitation_probability?.[i] ?? 0}%
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BestWindowsCard({
  king,
  halibut,
  loading,
  isFetching,
  updatedAt,
}: {
  king: BestHourWindow[];
  halibut: BestHourWindow[];
  loading?: boolean;
  isFetching?: boolean;
  updatedAt?: number;
}) {
  const [species, setSpecies] = useState<"king" | "halibut">("king");
  const list = species === "king" ? king : halibut;

  return (
    <div className="rounded-xl bg-card border border-border p-5 md:p-6 shadow-sm relative overflow-hidden">
      <span className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-primary via-[var(--color-ember)] to-primary opacity-80" />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[var(--color-ember)]">
            <Target size={16} className="shrink-0" />
            <p className="uppercase tracking-[0.2em] text-[11px] font-semibold">Best Fishing Windows</p>
            {isFetching && !loading && <Loader2 size={12} className="animate-spin opacity-80" aria-label="Refreshing" />}
          </div>
          <h3 className="font-display text-xl sm:text-2xl md:text-3xl mt-1 text-balance">
            Top hours next 36h — tides × barometer.
          </h3>
          <p className="text-xs text-muted-foreground mt-1 max-w-prose">
            Combines NOAA tide phase, Open-Meteo pressure trend, wind and species preferences to rank
            upcoming bite windows for Coffman Cove.
          </p>
        </div>
        <div className="flex gap-1 rounded-md border border-border bg-background p-1">
          {(["king", "halibut"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setSpecies(t)}
              className={`px-3.5 py-2.5 min-h-11 text-xs font-semibold uppercase tracking-wider rounded transition ${
                species === t
                  ? "bg-[var(--color-ember)] text-[var(--color-ember-foreground)]"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Fish size={12} className="inline mr-1.5 -mt-0.5" />
              {t === "king" ? "King Salmon" : "Halibut"}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-40 rounded-lg bg-muted/40 animate-pulse" />
          ))}
        </div>
      ) : list.length === 0 ? (
        <p className="mt-5 text-sm text-muted-foreground">No qualifying windows in the next 36 hours.</p>
      ) : (
        <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {list.map((w, i) => (
            <BestWindowTile key={w.startMs} window={w} rank={i + 1} />
          ))}
        </div>
      )}

      <div className="mt-4 pt-3 border-t border-border/60 space-y-1">
        {updatedAt ? (
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--color-ember)]">
            Updated {formatRelative(updatedAt)}
          </p>
        ) : null}
        <p className="text-[11px] font-mono text-muted-foreground break-words leading-snug">
          Open-Meteo hourly ({COFFMAN_COVE.lat.toFixed(3)}°N, {Math.abs(COFFMAN_COVE.lon).toFixed(3)}°W) +
          NOAA CO-OPS {COFFMAN_COVE.tideStation} ({COFFMAN_COVE.tideStationName}) · {COFFMAN_COVE.timezone}
        </p>
      </div>
    </div>
  );
}

function BestWindowTile({ window: w, rank }: { window: BestHourWindow; rank: number }) {
  const start = new Date(w.startMs);
  const end = new Date(w.endMs);
  const dayLabel = start.toDateString() === new Date().toDateString()
    ? "Today"
    : start.toLocaleDateString([], { weekday: "short" });
  const fmt = (d: Date) => d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const tone =
    w.score >= 75 ? "from-emerald-500/25 to-emerald-500/5 border-emerald-400/40"
    : w.score >= 60 ? "from-primary/25 to-primary/5 border-primary/40"
    : w.score >= 45 ? "from-amber-500/20 to-amber-500/5 border-amber-400/40"
    : "from-muted/40 to-transparent border-border";
  const phaseLabel = w.tidePhase === "slack-high" ? "Slack High"
    : w.tidePhase === "slack-low" ? "Slack Low"
    : w.tidePhase[0].toUpperCase() + w.tidePhase.slice(1);
  const TrendIcon = w.pressureTrend === "rising" ? ArrowUp : w.pressureTrend === "falling" ? ArrowDown : Minus;

  return (
    <div className={`rounded-lg border bg-gradient-to-br ${tone} p-3 flex flex-col gap-2`}>
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider opacity-80">
          <Clock size={11} /> {dayLabel} · {fmt(start)}–{fmt(end)}
        </span>
        <span className="inline-flex items-center gap-1 text-[10px] font-bold">
          #{rank} · {w.score}
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5 text-[10px]">
        <span className="inline-flex items-center gap-1 rounded-full bg-background/40 border border-border/60 px-2 py-0.5">
          <Waves size={10} /> {phaseLabel}
        </span>
        <span className="inline-flex items-center gap-1 rounded-full bg-background/40 border border-border/60 px-2 py-0.5">
          <Gauge size={10} /> {w.pressureMb.toFixed(0)} hPa <TrendIcon size={10} />
        </span>
        <span className="inline-flex items-center gap-1 rounded-full bg-background/40 border border-border/60 px-2 py-0.5">
          <Wind size={10} /> {w.windKn.toFixed(0)} kn
        </span>
      </div>
      <ul className="text-[11px] leading-snug space-y-0.5 opacity-95">
        {w.reasons.slice(0, 3).map((r, i) => (
          <li key={i} className="flex gap-1.5">
            <span className="opacity-60">•</span>
            <span>{r}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

